"use client";

import { useSyncExternalStore } from "react";
import dynamic from "next/dynamic";
import { type Activity } from "react-github-calendar";
import { siteConfig } from "@/config/site";

// Code-split: the calendar lib + its data layer only load once this (below the
// fold) component mounts, not as part of the initial page bundle.
const GitHubCalendar = dynamic(
  () => import("react-github-calendar").then((m) => m.GitHubCalendar),
  {
    ssr: false,
    loading: () => (
      <div className="h-24 w-full animate-pulse rounded bg-white/[0.04]" />
    ),
  },
);

// true once mounted on the client, false during SSR / first render — avoids
// hydration mismatch without a setState-in-effect.
const subscribe = () => () => {};
const useMounted = () =>
  useSyncExternalStore(
    subscribe,
    () => true,
    () => false,
  );

// GitHub green, dark ground only (site is dark-only).
const theme = {
  dark: ["#161b22", "#0e4429", "#006d32", "#26a641", "#39d353"],
  light: ["#161b22", "#0e4429", "#006d32", "#26a641", "#39d353"],
};

const getThreeMonthsAgo = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 3);
  return date;
};

const GitHubGraphs = () => {
  const mounted = useMounted();
  if (!mounted) return null;

  const transformThreeMonths = (data: Activity[]) => {
    const cutoff = getThreeMonthsAgo();
    return data.filter((activity) => new Date(activity.date) >= cutoff);
  };

  return (
    <>
      <div className="w-full md:hidden">
        <GitHubCalendar
          username={siteConfig.links.githubUsername}
          errorMessage="Could not fetch GitHub activity"
          showColorLegend={false}
          theme={theme}
          colorScheme="dark"
          showWeekdayLabels
          blockMargin={6}
          transformData={transformThreeMonths}
        />
      </div>

      <div className="hidden w-full md:block">
        <GitHubCalendar
          username={siteConfig.links.githubUsername}
          errorMessage="Could not fetch GitHub activity"
          theme={theme}
          colorScheme="dark"
          showWeekdayLabels
          blockMargin={6}
        />
      </div>
    </>
  );
};

export default GitHubGraphs;
